import type { PaymentReturn } from '../lib/deeplink'
import type { PendingBookingSnapshot } from '../lib/pendingBooking'
import type {
  CancelWidgetCheckoutResponse,
  ConfirmWidgetCheckoutSessionResponse,
  UUID,
} from '../types/PublicBookingChannelTypes'
import { interpretPaymentConfirmation } from './bookingSubmission'

/**
 * A customer coming back from Checkout without paying (ACA-101), and a widget
 * inside a frame picking a payment up without Stripe's return parameters
 * (ACA-99). Kept out of BookingWidget so the rules can be tested without
 * rendering anything.
 */

export interface CheckoutCancelApi {
  cancelCheckout: (bookingId: UUID, sessionId: string | null) => Promise<CancelWidgetCheckoutResponse>
}

export type CheckoutCancelOutcome =
  /** The hold is gone and the time is free: the customer picks a time again. */
  | { kind: 'released' }
  /** Paid after all, in another tab or just before leaving. */
  | { kind: 'paid'; status: 'CONFIRMED' | 'REQUESTED'; reference: string | null }
  /** The hold could not be released just now (or the call failed) and still stands. */
  | { kind: 'held' }

/** Asks the server to release the hold. Never throws: a failure leaves the hold as it was. */
export async function cancelCheckout(
  api: CheckoutCancelApi,
  bookingId: UUID,
  sessionId: string | null
): Promise<CheckoutCancelOutcome> {
  try {
    const response = await api.cancelCheckout(bookingId, sessionId)
    const reference = response.reference?.trim() || null
    switch (response.bookingStatus) {
      case 'EXPIRED':
        return { kind: 'released' }
      case 'CONFIRMED':
      case 'REQUESTED':
        return { kind: 'paid', status: response.bookingStatus, reference }
      default:
        return { kind: 'held' }
    }
  } catch {
    return { kind: 'held' }
  }
}

export type SnapshotResume =
  | { kind: 'none' }
  /**
   * Checkout replaced the whole page and that page has loaded again: ask
   * Stripe how it went. Unpaid means the customer left Checkout.
   */
  | { kind: 'confirm'; paymentReturn: PaymentReturn }
  /** Checkout is open in another tab: keep waiting for it here. */
  | { kind: 'waitInNewTab'; bookingId: UUID; sessionId: string; checkoutUrl: string }
  /**
   * Checkout replaced the whole page and ended on the hosted booking page
   * (ACA-111), which told the customer how it went. Settle it quietly: this is
   * a later visit, or a Back from Checkout, never the return itself.
   */
  | { kind: 'settleHosted'; bookingId: UUID; sessionId: string }

/**
 * What a page load with no Stripe parameters of its own should do with a
 * saved snapshot. Only a framed Checkout counts: an unframed one always comes
 * back with the parameters, so its snapshot alone means nothing new.
 */
export function resumeFromSnapshot(snapshot: PendingBookingSnapshot | null): SnapshotResume {
  const checkout = snapshot?.checkout
  const sessionId = snapshot?.held.sessionId
  if (!snapshot || !checkout || !sessionId) return { kind: 'none' }

  if (checkout.route === 'top') {
    if (checkout.endsOnHostedPage) {
      return { kind: 'settleHosted', bookingId: snapshot.held.bookingId, sessionId }
    }
    return {
      kind: 'confirm',
      paymentReturn: {
        isReturn: true,
        cancelled: false,
        sessionId,
        bookingId: snapshot.held.bookingId,
      },
    }
  }
  if (checkout.route === 'newTab') {
    return {
      kind: 'waitInNewTab',
      bookingId: snapshot.held.bookingId,
      sessionId,
      checkoutUrl: checkout.checkoutUrl,
    }
  }
  return { kind: 'none' }
}

export interface HostedCheckoutApi extends CheckoutCancelApi {
  confirmPayment: (bookingId: UUID, sessionId: string) => Promise<ConfirmWidgetCheckoutSessionResponse>
}

/**
 * A Checkout that ended on the hosted booking page (ACA-111), seen again from
 * the page that opened it. Paid, expired or still settling: the hosted page
 * has it, so there is nothing to pick up here. Unpaid: the customer came back
 * without paying (the browser's Back button), so the time is freed now rather
 * than when the hold runs out (ACA-101). Never throws.
 *
 * @returns true once nothing is left to pick up, so the snapshot can go; false
 *   when the server could not be asked, to try again on the next visit
 */
export async function settleHostedCheckout(
  api: HostedCheckoutApi,
  bookingId: UUID,
  sessionId: string
): Promise<boolean> {
  let unpaid: boolean
  try {
    unpaid = interpretPaymentConfirmation(await api.confirmPayment(bookingId, sessionId)).kind === 'unpaid'
  } catch {
    return false
  }
  if (unpaid) await cancelCheckout(api, bookingId, sessionId)
  return true
}
